<template>
  <div id="app" class="min-h-screen">
    <TheHeader @navigate="handleNavigate" :current-page="currentPage" />
    <keep-alive>
      <component :is="currentPageComponent" v-bind="currentPageProps" />
    </keep-alive>
    <TheFooter />
  </div>
</template>

<script>
import TheHeader from './components/TheHeader.vue'
import TheFooter from './components/TheFooter.vue'
import HomeView from './views/HomeView.vue'
import AboutView from './views/AboutView.vue'
import PartnersView from './views/PartnersView.vue'
import EquipmentView from './views/EquipmentView.vue'
import ProductView from './views/ProductView.vue'
import SpecsView from './views/SpecsView.vue'
import ContactView from './views/ContactView.vue'
import { getAboutConfigs, getAboutSpirits, getAboutHonors } from '@/api/about'
import { getPublicPartners, getPublicPartnerStats, getPublicPartnerConfigs, getPublicPartnerApplications } from '@/api/partner'
import { getPublicEquipmentAll } from '@/api/equipment'
import { getPublicContactAll } from '@/api/contact'

export default {
  name: 'App',
  components: {
    TheHeader,
    TheFooter,
    HomeView,
    AboutView,
    PartnersView,
    EquipmentView,
    ProductView,
    SpecsView,
    ContactView
  },
  data() {
    return {
      currentPage: 'home',
      // 全局预加载数据存储
      globalData: {
        about: null,
        partners: null,
        equipment: null,
        contact: null
      }
    }
  },
  computed: {
    currentPageComponent() {
      switch(this.currentPage) {
        case 'home': return 'HomeView';
        case 'about': return 'AboutView';
        case 'partners': return 'PartnersView';
        case 'equipment': return 'EquipmentView';
        case 'product': return 'ProductView';
        case 'specs': return 'SpecsView';
        case 'contact': return 'ContactView';
        default: return 'HomeView';
      }
    },
    // 将当前页面的预加载数据作为 props 传递
    currentPageProps() {
      if (this.currentPage === 'about') return { preloadedData: this.globalData.about };
      if (this.currentPage === 'partners') return { preloadedData: this.globalData.partners };
      if (this.currentPage === 'equipment') return { preloadedData: this.globalData.equipment };
      if (this.currentPage === 'contact') return { preloadedData: this.globalData.contact };
      return {};
    }
  },
  created() {
    // 监听全局导航事件
    this.$root.$on('navigate', this.handleNavigate);
    // 启动预加载
    this.prefetchAllData();
  },
  beforeDestroy() {
    this.$root.$off('navigate', this.handleNavigate);
  },
  methods: {
    async prefetchAllData() {
      try {
        // 并发请求所有页面的公共数据
        const [about, partners, equipment, contact] = await Promise.allSettled([
          this.fetchAboutData(),
          this.fetchPartnersData(),
          getPublicEquipmentAll(),
          getPublicContactAll()
        ]);

        if (about.status === 'fulfilled') this.globalData.about = about.value;
        if (partners.status === 'fulfilled') this.globalData.partners = partners.value;
        if (equipment.status === 'fulfilled') this.globalData.equipment = equipment.value.data;
        if (contact.status === 'fulfilled') this.globalData.contact = contact.value.data;
      } catch (error) {
        console.error('Prefetch failed:', error);
      }
    },
    async fetchAboutData() {
      const [configRes, spiritRes, honorRes] = await Promise.all([
        getAboutConfigs(),
        getAboutSpirits(),
        getAboutHonors()
      ]);
      return {
        configs: configRes.data,
        spirits: spiritRes.data,
        honors: honorRes.data
      };
    },
    async fetchPartnersData() {
      const [configRes, statRes, partnerRes, applicationRes] = await Promise.all([
        getPublicPartnerConfigs(),
        getPublicPartnerStats(),
        getPublicPartnerApplications(),
        getPublicPartners()
      ]);
      return {
        configs: configRes.data,
        stats: statRes.data,
        applications: applicationRes.data,
        list: partnerRes.data
      };
    },
    handleNavigate(page) {
      this.currentPage = page;
      // 页面切换时，强制滚动条回到顶部
      window.scrollTo(0, 0);
    }
  }
}
</script>

<style>
/* Global styles */
body {
  margin: 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
